import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Pill, Package, Check, X, Plus, Clock, AlertTriangle, Loader2
} from 'lucide-react';

const DispensacaoItemCard = ({
  item, saving, setSaving, prnQuantities, setPrnQuantities,
  prnObs, setPrnObs, onToggleSlot, onRegistrarPRN,
}) => {
  const isPRN = item.tipo_uso === 'se_necessario';
  const isNP = item.tipo_uso === 'nao_prescrito' || item.nao_prescrito;
  const isMed = item.item_tipo === 'medicamento';
  const Icon = isMed ? Pill : Package;
  const iconColor = isNP ? 'text-red-600' : isMed ? 'text-purple-600' : 'text-blue-600';

  return (
    <Card className={`mb-3 ${isNP ? 'border-red-300 dark:border-red-700' : ''}`} data-testid={`dispensacao-item-${item.regra_id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${iconColor}`} />
            <div>
              <span className="font-semibold">{item.item_nome}</span>
              <div className="flex items-center gap-2 mt-0.5">
                {isNP ? (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Não prescrito
                  </Badge>
                ) : (
                  <Badge variant="outline" className={isPRN ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}>
                    {isPRN ? 'Se necessário' : 'Programado'}
                  </Badge>
                )}
                {!isNP && (
                  <span className="text-xs text-muted-foreground">
                    {item.frequencia} | {item.quantidade_por_aplicacao} {item.item_unidade}/dose
                  </span>
                )}
              </div>
              {item.observacoes_regra && (
                <p className="text-xs text-muted-foreground mt-1">{item.observacoes_regra}</p>
              )}
            </div>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <span>Estoque: {item.estoque_atual} {item.item_unidade}</span>
          </div>
        </div>

        {/* Scheduled: dose slots */}
        {!isPRN && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {item.slots.map((slot) => {
              const key = `${item.regra_id}_${slot.slot_index}`;
              const isAdm = slot.status === 'administrado';
              const isNaoAdm = slot.status === 'nao_administrado';
              const isPendente = slot.status === 'pendente';
              const isSavingThis = saving === key;

              return (
                <button
                  key={slot.slot_index}
                  onClick={() => onToggleSlot(item, slot)}
                  disabled={isSavingThis}
                  className={`
                    flex flex-col items-center justify-center rounded-lg border-2 p-3 transition-all
                    ${isAdm ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}
                    ${isNaoAdm ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : ''}
                    ${isPendente ? 'border-dashed border-gray-300 bg-white dark:bg-gray-800 hover:border-green-400 hover:bg-green-50/50' : ''}
                    ${isSavingThis ? 'opacity-50' : 'cursor-pointer'}
                  `}
                  data-testid={`slot-${item.regra_id}-${slot.slot_index}`}
                >
                  {isSavingThis ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  ) : isAdm ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : isNaoAdm ? (
                    <X className="h-5 w-5 text-red-500" />
                  ) : (
                    <Clock className="h-5 w-5 text-gray-400" />
                  )}
                  <span className="text-xs mt-1 font-medium">Dose {slot.slot_index + 1}</span>
                  <span className="text-[10px] text-muted-foreground">{slot.quantidade} {item.item_unidade}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* PRN: registration */}
        {isPRN && (
          <div className="space-y-3">
            {item.prn_registros.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Registros de hoje:</Label>
                {item.prn_registros.map((reg, idx) => (
                  <div key={reg.dispensacao_id || idx} className="flex items-center gap-2 text-sm p-2 rounded bg-green-50 dark:bg-green-900/10 border border-green-200">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>{reg.quantidade} {item.item_unidade}</span>
                    {reg.observacoes && <span className="text-muted-foreground">— {reg.observacoes}</span>}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {reg.criado_em ? new Date(reg.criado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-2 p-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 dark:bg-amber-900/10">
              <div className="space-y-1 flex-1 max-w-[100px]">
                <Label className="text-xs">Quantidade</Label>
                <Input
                  type="number" step="0.5" min="0.5"
                  value={prnQuantities[item.regra_id] || ''}
                  onChange={(e) => setPrnQuantities(prev => ({ ...prev, [item.regra_id]: e.target.value }))}
                  placeholder={String(item.quantidade_por_aplicacao || 1)}
                  className="h-8"
                  data-testid={`prn-qty-${item.regra_id}`}
                />
              </div>
              <div className="space-y-1 flex-1">
                <Label className="text-xs">Obs (opcional)</Label>
                <Input
                  value={prnObs[item.regra_id] || ''}
                  onChange={(e) => setPrnObs(prev => ({ ...prev, [item.regra_id]: e.target.value }))}
                  placeholder="Observação..."
                  className="h-8"
                  data-testid={`prn-obs-${item.regra_id}`}
                />
              </div>
              <Button
                size="sm"
                onClick={() => onRegistrarPRN(item)}
                disabled={saving === `prn_${item.regra_id}`}
                className="h-8 gap-1"
                data-testid={`btn-registrar-prn-${item.regra_id}`}
              >
                {saving === `prn_${item.regra_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Registrar uso
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DispensacaoItemCard;
